import { ref } from 'fine-jsx'
import * as Vue from 'vue'

import { renderComponentTo } from 'shared/video/render-to'

import type * as pub from '../../types/webgl-video-editor.ts'
import type { ImportMediaBin } from '../components/utils.ts'
import { VideoEditorUI } from '../components/video-editor-ui.jsx'
import styles from '../css/index.module.css'
import { fromVue, toVue } from '../document-views/vue/utils.ts'
import { LocalSync as VideoEditorLocalStore_ } from '../sync/index.ts'
import { VideoEditor } from '../video-editor.ts'

import { editorToVue } from './vue-video-editor.ts'

export * from '../constants.ts'

export default Vue.defineComponent({
  name: 'VideoEditor',
  props: {
    editor: {
      type: Object as Vue.PropType<pub.VideoEditor>,
      required: false,
    },
    sync: {
      type: Object as Vue.PropType<pub.VideoEditorDocumentSync>,
      required: false,
    },
    assets: {
      type: Object as Vue.PropType<pub.VideoEditorAssetStore>,
      required: false,
    },
    onClickHelp: {
      type: Function as Vue.PropType<() => unknown>,
      required: false,
    },
    importMediaFiles: {
      type: Function as Vue.PropType<(bin: ImportMediaBin) => Promise<File[]>>,
      required: false,
    },
    messages: { type: Object as Vue.PropType<Record<string, Record<string, string>>>, required: false },
    languages: { type: Array as Vue.PropType<string[]>, required: false },
  },
  emits: ['error'],
  setup(props, ctx) {
    const editor = props.editor
      ? editorToVue(props.editor, false)
      : new VideoEditorVue({
          sync: props.sync,
          assets: props.assets,
        })
    const container = Vue.ref<HTMLElement>()

    const children = {
      header: ref<unknown>(),
      timelineEmpty: ref<unknown>(),
    }

    Vue.watch(container, (host, _prev, onCleanup) => {
      if (!host) return

      host.classList.add(styles.host)

      const stop = renderComponentTo(
        VideoEditorUI,
        {
          editor,
          children,
          onClickHelp: props.onClickHelp,
          importMediaFiles: props.importMediaFiles,
          i18n: {
            messages: fromVue(() => props.messages ?? {}),
            languages: fromVue(() => props.languages),
          },
        },
        host,
      )

      onCleanup(stop)
    })

    ctx.expose(editor)

    Vue.onScopeDispose(() => editor.dispose())

    const { h } = Vue
    const { slots } = ctx

    const createSlotNode = (name: keyof typeof children) =>
      slots[name] &&
      h(
        'div',
        {
          ref: (el_) => {
            const el = el_ as HTMLElement | null
            if (el && el.parentNode === container.value) el.remove()
            children[name].value = el
          },
        },
        h(slots[name]),
      )

    return () =>
      h('div', { ...ctx.attrs, ref: container }, [createSlotNode('header'), createSlotNode('timelineEmpty')])
  },
})

/* eslint-disable no-constructor-return -- return object adapted for vue */
class VideoEditorVue extends VideoEditor {
  constructor(...args: ConstructorParameters<typeof pub.VideoEditor>) {
    super(...args)
    return editorToVue(this, true) as VideoEditor
  }
}
/* eslint-enable no-constructor-return */

export { VideoEditorVue as VideoEditor }

export class VideoEditorLocalStore extends VideoEditorLocalStore_ {
  readonly #canUndo = toVue(() => super.canUndo)
  readonly #canRedo = toVue(() => super.canRedo)

  get canUndo(): boolean {
    return this.#canUndo.value
  }
  get canRedo(): boolean {
    return this.#canRedo.value
  }

  constructor() {
    super()
    Vue.markRaw(this)
  }
}
